import { useEffect, useState } from "react";
import { Receipt, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { http, formatApiError } from "../../lib/api";
import { fmtTL } from "../../lib/format";
import { toast } from "../../lib/notify";

const key = (r) => `${r.subcontractor_id}|${r.category_id || ""}`;

// "Tümü için oluştur" önizlemesi: her taşeronun tutarı, müşteri faturası / kapalı yıl / mevcut hakediş engelleri; seçilenler onayla üretilir
export default function BulkClaimPreview({ open, onClose, params, body, onDone }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setData(null); setResult(null); return; }
    http.get("/reports/subcontractor-claim/bulk-preview", { params }).then((r) => {
      setData(r.data);
      setPicked(new Set(r.data.rows.filter((x) => x.can_create).map(key)));
    }).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (r) => setPicked((p) => { const n = new Set(p); n.has(key(r)) ? n.delete(key(r)) : n.add(key(r)); return n; });
  const selected = (data?.rows || []).filter((r) => r.can_create && picked.has(key(r)));
  const selectedTotal = selected.reduce((a, r) => a + r.total, 0);

  const create = async () => {
    setBusy(true);
    try {
      const { data: res } = await http.post("/reports/subcontractor-claim/create-invoice-bulk", { ...body, rows: selected.map((r) => ({ subcontractor_id: r.subcontractor_id, category_id: r.category_id || null })) });
      setResult(res);
      if (res.created.length) toast.success(`${res.created.length} taşeron hakedişi oluşturuldu (${fmtTL(res.created_total)})`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="bulk-claim-preview">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-amber-600" /> Toplu Taşeron Hakedişi — Önizleme {data ? `(${data.period})` : ""}</DialogTitle>
          <DialogDescription>Seçili taşeronlar için ayrı ayrı hakediş faturası oluşturulur. Engeli olan satırlar üretilemez; nedeni yanında yazar.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-sm text-slate-500">Hesaplanıyor…</div> : result ? (
          <div className="space-y-3" data-testid="bulk-claim-result">
            {result.created.map((c) => <div key={c.invoice_id} className="flex items-center gap-2 text-sm text-emerald-800"><CheckCircle2 className="w-4 h-4" /> <b>{c.invoice_no}</b> · {c.subcontractor_name} · {c.category_name} · {fmtTL(c.total)}</div>)}
            {result.errors.map((e, i) => <div key={i} className="flex items-center gap-2 text-sm text-red-700"><AlertTriangle className="w-4 h-4" /> {e.subcontractor_name} · {e.category_name}: {e.message}</div>)}
            {!result.created.length && !result.errors.length && <div className="text-sm text-slate-500">Oluşturulacak satır yoktu.</div>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md ${data.customer_invoice.exists ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="bulk-claim-customer-status">
              {data.customer_invoice.exists ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {data.customer_invoice.exists ? <>Müşteri hakedişi mevcut ({data.customer_invoice.invoice_no}{data.customer_invoice.approved ? " · onaylı" : " · taslak"})</> : <>Bu proje / dönem için müşteri faturası yok — taşeron hakedişi oluşturulamaz</>}
            </div>
            <div className="max-h-[50vh] overflow-auto border border-slate-200 rounded-md">
              <table className="w-full tbl">
                <thead className="bg-slate-50 sticky top-0"><tr>{["", "Taşeron", "Ana Başlık", "Kayıt", "Tutar", "Durum"].map((h, i) => <th key={h || i} className={`px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold ${i === 3 || i === 4 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={key(r)} className={`border-t border-slate-100 ${!r.can_create ? "text-slate-400" : ""}`} data-testid={`bulk-claim-row-${r.subcontractor_id}`} data-can={r.can_create ? "1" : "0"}>
                      <td className="px-3 py-1.5"><input type="checkbox" disabled={!r.can_create} checked={r.can_create && picked.has(key(r))} onChange={() => toggle(r)} data-testid={`bulk-claim-pick-${r.subcontractor_id}`} /></td>
                      <td className="px-3 py-1.5 text-xs font-semibold">{r.subcontractor_name}</td>
                      <td className="px-3 py-1.5 text-xs">{r.category_name}</td>
                      <td className="numeric-cell px-3 py-1.5 text-xs">{r.count}</td>
                      <td className="numeric-cell px-3 py-1.5 text-xs font-semibold">{fmtTL(r.total)}</td>
                      <td className="px-3 py-1.5 text-xs">{r.can_create ? <span className="text-emerald-700 font-semibold">Oluşturulacak</span> : <span className="text-red-700">{r.issue}{r.issue_link && <a href={r.issue_link.replace("__BACK__", encodeURIComponent(window.location.pathname + window.location.search))} className="inline-flex items-center gap-1 ml-1 underline" data-testid={`bulk-claim-issue-link-${r.subcontractor_id}`}><ExternalLink className="w-3 h-3" />git</a>}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-sm flex items-center justify-between">
              <span className="text-slate-600">Seçili <b data-testid="bulk-claim-selected-count">{selected.length}</b> taşeron · toplam <b data-testid="bulk-claim-selected-total">{fmtTL(selectedTotal)}</b>{data.blocked > 0 && <span className="text-red-700"> · {data.blocked} satır engelli</span>}</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <button type="button" onClick={onClose} className="px-3.5 py-2 rounded-md border border-slate-300 text-sm font-semibold hover:bg-slate-100" data-testid="bulk-claim-close">{result ? "Kapat" : "Vazgeç"}</button>
          {!result && <button type="button" onClick={create} disabled={busy || !selected.length} data-testid="bulk-claim-confirm"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"><Receipt className="w-4 h-4" /> {busy ? "Oluşturuluyor…" : `Onayla ve oluştur (${selected.length})`}</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
